/**
 * How an event's dates read across the sign-off screens.
 *
 * Extracted from the sign-off page once a second screen needed the same
 * wording: the copy that had been made for the editor's Sign-off tab already
 * printed `10/01/2026` where the page printed `10/1/2026`, from the same
 * timestamp, on the same chain.
 */

/** A `Date`, or null for anything that will not parse. */
export const asDate = (value: string | undefined): Date | null => {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * `Thu, 1/1/2026`.
 *
 * The weekday comes from `Intl`, so it is whatever the locale says rather than
 * a list of abbreviations maintained here. The numbers are assembled by hand
 * only because en-AU's numeric date is zero-padded — `01/01/2026` — and the
 * format asked for is not.
 */
export const dayOf = (d: Date) =>
	`${d.toLocaleDateString("en-AU", { weekday: "short" })}, ` +
	`${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;

/** `1:00 pm`, from the locale. */
export const timeOf = (d: Date) =>
	d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });

/* Midnight to midnight is how this app stores a date-only event, so a time
   against one would be noise dressed as information. */
export const atMidnight = (d: Date | null) =>
	!!d && d.getHours() === 0 && d.getMinutes() === 0;

/** `Thu, 1/1/2026 1:00 pm`, or just the date when the event has no time. */
export const formatWhen = (value: string | undefined): string => {
	const at = asDate(value);
	if (!at) return "";
	return atMidnight(at) ? dayOf(at) : `${dayOf(at)} ${timeOf(at)}`;
};

/**
 * The day on one line, the times on another.
 *
 * "Mon, 31/8/2026 9:00 am" on one line does not fit the list's date column and
 * ran over the progress bar beside it. The prototype splits them the same way,
 * and the detail panel joins the two back together, where there is room.
 */
export const whenLines = (start?: string, end?: string): [string, string] => {
	const from = asDate(start);
	if (!from) return ["", ""];
	if (atMidnight(from)) return [dayOf(from), "All day"];
	const to = asDate(end);
	const sameDay = !!to && to.toDateString() === from.toDateString();
	return [
		dayOf(from),
		to
			? sameDay
				? `${timeOf(from)} - ${timeOf(to)}`
				: /* Multi-day: the end DATE matters and the start time does not,
				     and both together do not fit the column. */
					`to ${dayOf(to)}`
			: timeOf(from),
	];
};
